import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Empty, Tooltip, message, Image, Spin } from 'components/shared/AntComponent';
import { Input } from 'components/shared/FormComponent';
import { Button } from 'components/shared/ButtonComponent';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
    getTicket, 
    saveTicket, 
    saveTicketConversation, 
    getTicketConversation, 
    getTicketHistory 
} from 'services/actions/ticketActions';
import dayjs from 'dayjs';
import { resetSaveTicketState } from 'services/slices/ticketSlice';
import { ResetIcon } from 'assets/img/custom-icons';

interface TicketHistoryEntry {
    status: string; 
    changed_on: string;
    changed_by?: string;
    note?: string;
}

interface TicketConversationItem {
    id: number;
    ticketid: number;
    message: string;
    user_id: number;
    user_name: string;
    user_type: string;
    attachment?: string | null;
    is_internal: boolean;
    is_active: boolean;
    created_on: string;
    modified_on: string;
    createdby: number;
    modifiedby: number;
}

interface TicketRecord {
    id?: number;
    name?: string;
    category?: string;
    description?: string;
    status?: string;
    priority?: string;
    created_on?: string;
    updated_on?: string;
    requester_name?: string;
    created_by?: string;
    assigned_to?: string;
    filepath?: string | string[] | null;
    ticket_from?: string;
    accountid?: number;
    encoded_ticketid?: number;
    history?: TicketHistoryEntry[];
    activityLog?: TicketHistoryEntry[];
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    open: { label: 'Open', className: 'status-open' },
    inprogress: { label: 'In Progress', className: 'status-inprogress' },
    onhold: { label: 'On Hold', className: 'status-onhold' },
    resolved: { label: 'Resolved', className: 'status-resolved' },
    closed: { label: 'Closed', className: 'status-closed' },
};

const normalizeFilePaths = (filepath?: string | string[] | null): string[] => {
    if (!filepath) return [];
    if (Array.isArray(filepath)) return filepath.filter(Boolean);
    return [filepath];
};

const getStatusConfig = (status?: string) => {
    const key = (status || '').toLowerCase().replace(/\s+/g, '');
    return STATUS_CONFIG[key] || { label: status || '-', className: 'status-default' };
};

const getInlineStatusStyle = (status?: string) => {
    const key = (status || '').toLowerCase().replace(/\s+/g, '');
    const styleMap: Record<string, { bg: string; text: string; dot: string }> = {
        open: { bg: '#d3edf9', text: '#1890ff', dot: '#1890ff' },
        inprogress: { bg: '#fff1d4', text: '#fa8c16', dot: '#fa8c16' },
        onhold: { bg: '#dfdfdf', text: '#595959', dot: '#595959' },
        resolved: { bg: '#e4ffc8', text: '#52c41a', dot: '#52c41a' },
        closed: { bg: '#FFD6D3', text: '#f5222d', dot: '#f5222d' },
    };
    return styleMap[key] || { bg: '#f5f5f5', text: '#595959', dot: '#595959' };
};

const formatTrackingDate = (status: string, isoDate: string) => {
    const isClosed = getStatusConfig(status).label === 'Closed';
    return isClosed ? dayjs(isoDate).format('MMM D, YYYY · hh:mm A') : dayjs(isoDate).format('MMM D, YYYY');
};

const cleanFileNameAndExt = (urlStr: string) => {
    if (!urlStr) return { fileName: 'Attachment', ext: 'FILE' };
    try {
        const decodedUrl = decodeURIComponent(urlStr);
        const baseUrl = decodedUrl.split('?')[0];
        const rawFileName = baseUrl.split('/').pop() || 'Attachment';
        const extMatch = rawFileName.match(/\.([a-zA-Z0-9]+)$/);
        const ext = extMatch ? extMatch[1].toLowerCase() : 'file';
        return { fileName: rawFileName, ext };
    } catch {
        return { fileName: 'Attachment', ext: 'file' };
    }
};

const parseQuotedMessage = (rawText: string) => {
    if (rawText.startsWith('[[REPLY_TO:') && rawText.includes(']]\n')) {
        const headerEnd = rawText.indexOf(']]\n');
        const meta = rawText.substring(11, headerEnd);
        const actualText = rawText.substring(headerEnd + 3);
        const colonIdx = meta.indexOf(': ');
        if (colonIdx > -1) {
            return {
                replyUser: meta.substring(0, colonIdx),
                replyText: meta.substring(colonIdx + 2),
                actualText
            };
        }
    }
    return null;
};

const formatChatDateSeparator = (dateStr: string) => {
    const date = dayjs(dateStr);
    const today = dayjs().startOf('day');
    const yesterday = dayjs().subtract(1, 'day').startOf('day');

    if (date.isSame(today, 'day')) {
        return 'Today';
    } else if (date.isSame(yesterday, 'day')) {
        return 'Yesterday';
    } else {
        return date.format('MMMM D, YYYY');
    }
};

const PaperclipIcon = ({ size = 18, color = '#595959' }: { size?: number; color?: string }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: 'block', transform: 'rotate(-45deg)' }}
    >
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
);

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'];

const TicketDetails: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<any>();
    const { 
        ticketHistoryInfo, 
        saveTicketSuccess, 
        saveTicketLoading, 
        getTicketConversationLoading, 
        getTicketConversationInfo, 
        saveTicketConversationLoading,
        saveTicketConversationSuccess
    } = useSelector((state: any) => state.ticket);

    const userRole = sessionStorage.getItem('role');
    const loggedInUserId = Number(sessionStorage.getItem('userid') || 0);
    const firstName = sessionStorage.getItem('firstname') || '';
    const lastName = sessionStorage.getItem('lastname') || '';
    const loggedInUserName = `${firstName} ${lastName}`.trim() || sessionStorage.getItem('username') || 'User';

    const rowData: TicketRecord | undefined = location.state?.rowData;
    const [newMessage, setNewMessage] = useState('');
    const [replyingTo, setReplyingTo] = useState<TicketConversationItem | null>(null);

    const [chatFile, setChatFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const chatEndRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<any>(null);

    const [persistedConversations, setPersistedConversations] = useState<TicketConversationItem[]>([]);

    useEffect(() => {
        if (rowData?.id) {
            setPersistedConversations([]);
            setReplyingTo(null);
            setChatFile(null);
            setNewMessage('');
            if (fileInputRef.current) fileInputRef.current.value = '';

            dispatch(getTicket(rowData.id) as any);
            dispatch(getTicketHistory(rowData.id) as any);
            dispatch(getTicketConversation({ ticketid: rowData.id }) as any);
        }
    }, [rowData?.id, dispatch]);

    const record: TicketRecord = useMemo(() => {
        const historyTicket = ticketHistoryInfo?.ticket;
        const isCurrentTicket = historyTicket && Number(historyTicket.id) === Number(rowData?.id);
        return {
            ...rowData,
            ...(isCurrentTicket ? historyTicket : {}),
        };
    }, [rowData, ticketHistoryInfo]);

    const conversations: TicketConversationItem[] = useMemo(() => {
        const list: TicketConversationItem[] = getTicketConversationInfo?.data || [];
        const filteredList = list.filter((item) => Number(item.ticketid) === Number(rowData?.id));
        
        return filteredList.slice().sort(
            (a: TicketConversationItem, b: TicketConversationItem) => 
                dayjs(a.created_on).valueOf() - dayjs(b.created_on).valueOf()
        );
    }, [getTicketConversationInfo, rowData?.id]);

    useEffect(() => {
        if (conversations.length > 0) {
            setPersistedConversations(conversations);
        }
    }, [conversations]);

    const activeList = conversations.length > 0 ? conversations : persistedConversations;

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [activeList]);

    const handleBack = () => navigate('/tickets');

    const handleEditFromDetail = () => {
        navigate('/tickets/add-ticket', { state: { rowData: record } });
    };

    const handleRefreshConversation = () => {
        if (rowData?.id) {
            dispatch(getTicketConversation({ ticketid: rowData.id }) as any);
        }
    };

    useEffect(() => {
        if (saveTicketConversationSuccess && rowData?.id) {
            setNewMessage('');
            setReplyingTo(null);
            setChatFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            dispatch(getTicketConversation({ ticketid: rowData.id }) as any);
        }
    }, [saveTicketConversationSuccess, rowData?.id, dispatch]);

    const handleReplyClick = (item: TicketConversationItem) => {
        setReplyingTo(item);
        if (inputRef.current) {
            inputRef.current.focus?.();
        }
    };

    const handleCancelReply = () => {
        setReplyingTo(null);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const ext = file.name.split('.').pop()?.toLowerCase() || '';

            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                message.error(`Files with .${ext} extension are not allowed.`);
                if (fileInputRef.current) fileInputRef.current.value = '';
                return;
            }

            if (file.size > 10 * 1024 * 1024) {
                message.error('File size exceeds 10MB limit.');
                if (fileInputRef.current) fileInputRef.current.value = '';
                return;
            }

            setChatFile(file);
        }
    };

    const handleRemoveFile = () => {
        setChatFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSendMessage = async () => {
        const trimmed = newMessage.trim();
        if ((!trimmed && !chatFile) || !record?.id) return;

        let finalMessage = trimmed;
        if (replyingTo) {
            const author = replyingTo.user_name || replyingTo.user_type || 'User';
            const cleanQuote = replyingTo.message.replace(/\[\[REPLY_TO:[^\]]+\]\]\n/g, '').trim().slice(0, 80);
            finalMessage = `[[REPLY_TO:${author}: ${cleanQuote}]]\n${finalMessage || (chatFile ? `Sent a file: ${chatFile.name}` : '')}`;
        }

        const inputJson = {
            ticketid: record.id,
            message: finalMessage,
            user_id: loggedInUserId,
            user_name: loggedInUserName,
            user_type: (userRole || 'USER').toUpperCase(),
            is_internal: false,
            is_active: true,
            createdby: loggedInUserId,
            modifiedby: loggedInUserId
        };

        const formData = new FormData();
        formData.append("InputJson", JSON.stringify(inputJson));

        if (chatFile) {
            formData.append('File', chatFile, chatFile.name);
        } else {
            formData.append(
                'File',
                new File([], 'empty.txt', { type: 'text/plain' }),
                'empty.txt'
            );
        }

        try {
            const response = await dispatch(saveTicketConversation(formData) as any).unwrap();

            if (response?.status === "success") {
                setNewMessage(''); 
                setReplyingTo(null);
                setChatFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                if (rowData?.id) {
                    dispatch(getTicketConversation({ ticketid: rowData.id }) as any); 
                }
            }
        } catch (err: any) {
            if (err?.status === "success" || err?.payload?.status === "success") {
                setNewMessage('');
                setReplyingTo(null);
                setChatFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                if (rowData?.id) {
                    dispatch(getTicketConversation({ ticketid: rowData.id }) as any);
                }
            } else {
                message.error(err?.message || 'Failed to send message');
            }
        }
    };

    if (!rowData) {
        return (
            <div className="ticket-detail-wrapper p-2">
                <div className="detail-header-card">
                    <Empty description="No ticket selected. Go back to the list and pick a ticket to view." />
                    <div className="text-center mt-3">
                        <Button className="add-btn" onClick={handleBack}>
                            Back to tickets
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const { label: statusLabel, className: statusClassName } = getStatusConfig(record.status);
    const headerStyles = getInlineStatusStyle(record.status);
    const filePaths = normalizeFilePaths(record.filepath);

    const history: TicketHistoryEntry[] = useMemo(() => {
        const isCurrentTicket = Number(ticketHistoryInfo?.ticket?.id) === Number(rowData?.id);
        const raw = isCurrentTicket ? (ticketHistoryInfo?.ticket?.history || []) : [];
        return raw.slice().sort(
            (a: any, b: any) => dayjs(a.changed_on).valueOf() - dayjs(b.changed_on).valueOf()
        );
    }, [ticketHistoryInfo, rowData?.id]);

    const handleTicketStatus = async (status: "closed" | "open") => {
        const formData = new FormData();
        const existingFilePaths = normalizeFilePaths(record.filepath);

        const inputJson = {
            id: record.id,
            name: record.name,
            category: record.category,
            description: record.description,
            priority: record.priority,
            status,
            is_active: true,
            assigned_to: record.assigned_to || null,
            requester_name: record.requester_name,
            ticket_from: record?.ticket_from,
            accountid: record?.accountid,
            filepath: existingFilePaths, 
        };

        formData.append("InputJson", JSON.stringify(inputJson));
        dispatch(saveTicket(formData) as any);
    };

    useEffect(() => {
        if (saveTicketSuccess) {
            message.success(record.status === "resolved" ? "Ticket closed successfully" : "Ticket reopened successfully");
            dispatch(resetSaveTicketState());
            navigate('/tickets');
        }
    }, [saveTicketSuccess, record.status, dispatch, navigate]);

    return (
        <div className="ticket-detail-wrapper p-2">
            <div className="detail-header-card">
                <div className="d-flex align-items-start justify-content-between flex-wrap mt-2">
                    <div>
                        <h4 className="detail-title">{record.name || 'Untitled ticket'}</h4>
                        <div className="detail-meta-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {record.id !== undefined && (
                                <span className="ticket-id-chip">#Ticket id: {record?.encoded_ticketid}</span>
                            )}
                            <span className={`category-chip category-${(record.category || '').toLowerCase()}`}>
                                {record.category?.toLowerCase() === 'bug'
                                    ? 'Issue'
                                    : record.category?.toLowerCase() === 'feature'
                                    ? 'Clarification'
                                    : (record.category || '-')}
                            </span>
                            <span 
                                className={`status-badge ${statusClassName}`}
                                style={{
                                    backgroundColor: headerStyles.bg,
                                    color: headerStyles.text,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '4px 12px',
                                    borderRadius: '50px',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    textTransform: 'capitalize'
                                }}
                            >
                                <span 
                                    className="status-dot" 
                                    style={{
                                        backgroundColor: headerStyles.dot,
                                        width: '7px',
                                        height: '7px',
                                        borderRadius: '50%',
                                        marginRight: '6px',
                                        display: 'inline-block'
                                    }}
                                />
                                {statusLabel}
                            </span>
                        </div>
                    </div>

                    <div className="ms-auto d-flex title-btn">
                        {userRole === "admin" && (
                            <Button className="add-btn outline-btn me-2" onClick={handleEditFromDetail}>
                                Edit Ticket
                            </Button>
                        )}
                        {userRole === "staff" && record.status === "resolved" && (
                            <Button className="add-btn me-2" loading={saveTicketLoading} onClick={() => handleTicketStatus("closed")}>
                                Mark as Closed
                            </Button>
                        )}
                        {userRole === "staff" && record.status === "closed" && (
                            <Button className="add-btn outline-btn me-2" loading={saveTicketLoading} onClick={handleEditFromDetail}>
                                Reopen Ticket
                            </Button>
                        )}
                        <Button type="primary" className="add-btn" onClick={handleBack}>
                            Back to tickets
                        </Button>
                    </div>
                </div>
            </div>

            <div 
                className="detail-body-grid" 
                style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1.5fr 1fr 1fr', 
                    gap: '12px', 
                    marginTop: '16px',
                    alignItems: 'stretch'
                }}
            >
                <div 
                    className="detail-main-card bg-white p-3 rounded shadow-sm d-flex flex-column visible-scrollbar"
                    style={{ height: '680px', overflowY: 'scroll', paddingRight: '10px' }}
                >
                    <h6 className="panel-title fw-bold mb-2">Description</h6>
                    <p className="detail-description mb-4">{record.description || 'No description provided.'}</p>

                    <div className="attachment-block">
                        <h6 className="panel-title fw-bold mb-3">Attachment</h6>
                        {filePaths.length > 0 ? (
                            <div className="d-flex flex-column gap-3">
                                {filePaths.map((filePath, index) => {
                                    const { fileName, ext } = cleanFileNameAndExt(filePath);
                                    const isImage = ['jpg', 'jpeg', 'png'].includes(ext);

                                    if (isImage) {
                                        return (
                                            <div key={`${filePath}-${index}`}>
                                                <Image
                                                    src={filePath}
                                                    alt={fileName}
                                                    className="attachment-preview"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '180px',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        objectFit: 'cover',
                                                    }}
                                                    preview={{ mask: 'Click to Preview' }}
                                                />
                                                <div className="mt-1 text-muted" style={{ fontSize: '13px' }}>
                                                    {fileName}
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div
                                            key={`${filePath}-${index}`}
                                            className="p-2 border rounded bg-light d-flex align-items-center justify-content-between"
                                        >
                                            <div className="d-flex align-items-center me-2" style={{ overflow: 'hidden', minWidth: 0 }}>
                                                <span className="badge bg-primary me-2 text-uppercase">{ext.toUpperCase()}</span>
                                                <span className="text-truncate" style={{ maxWidth: '150px' }} title={fileName}>
                                                    {fileName}
                                                </span>
                                            </div>
                                            <a
                                                href={filePath}
                                                download={fileName}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-outline-primary flex-shrink-0"
                                            >
                                                Download
                                            </a>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <span className="text-muted">No attachment available</span>
                        )}
                    </div>
                </div>

                <div 
                    className="detail-chat-card bg-white rounded shadow-sm d-flex flex-column" 
                    style={{ height: '680px', border: '1px solid #e0e0e0', overflow: 'hidden' }}
                >
                    <div 
                        className="p-3 border-bottom d-flex align-items-center justify-content-between flex-shrink-0" 
                        style={{ backgroundColor: '#DFEAFA', color: '#fff' }}
                    >
                        <div className="d-flex align-items-center">
                            <div 
                                style={{ 
                                    width: '38px', 
                                    height: '38px', 
                                    borderRadius: '50%', 
                                    background: '#5A53B2', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    fontWeight: 'bold' 
                                }}
                            >
                                💬
                            </div>
                            <div className="ms-2">
                                <h6 className="m-0 text-dark" style={{ fontSize: '15px' }}>Conversation</h6>
                                <small className="text-muted" style={{ fontSize: '11px' }}>Support Chat</small>
                            </div>
                        </div>

                        <Tooltip title="Refresh conversation">
                            <button
                                type="button"
                                onClick={handleRefreshConversation}
                                disabled={getTicketConversationLoading}
                                className="btn btn-sm d-flex align-items-center justify-content-center"
                                style={{
                                    border: 'none',
                                    background: 'rgba(255, 255, 255, 0.7)',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    padding: 0,
                                    cursor: getTicketConversationLoading ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <span className={getTicketConversationLoading ? "spin-refresh-icon" : ""}>
                                    <ResetIcon />
                                </span>
                            </button>
                        </Tooltip>
                    </div>

                    <div 
                        className="p-3 flex-grow-1 overflow-auto d-flex flex-column gap-2 visible-scrollbar"
                    >
                        {getTicketConversationLoading && activeList.length === 0 ? (
                            <div className="d-flex justify-content-center align-items-center h-100">
                                <Spin />
                            </div>
                        ) : activeList.length > 0 ? (
                            activeList.map((item, index) => {
                                const isMe = (item.user_id === loggedInUserId || item.createdby === loggedInUserId);
                                const quoteData = parseQuotedMessage(item.message);

                                const currentDate = dayjs(item.created_on).format('YYYY-MM-DD');
                                const prevDate = index > 0 ? dayjs(activeList[index - 1].created_on).format('YYYY-MM-DD') : null;
                                const showDateSeparator = currentDate !== prevDate;

                                const hasRealAttachment = item.attachment && !item.attachment.toLowerCase().includes('empty');
                                const attachFileInfo = hasRealAttachment ? cleanFileNameAndExt(item.attachment!) : null;
                                const isAttachImage = attachFileInfo ? ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(attachFileInfo.ext) : false;

                                return (
                                    <React.Fragment key={item.id}>
                                        {showDateSeparator && (
                                            <div className="d-flex justify-content-center my-2">
                                                <span className="date-separator-badge">
                                                    {formatChatDateSeparator(item.created_on)}
                                                </span>
                                            </div>
                                        )}

                                        <div
                                            className="chat-bubble-container d-flex align-items-center gap-1"
                                            style={{
                                                alignSelf: isMe ? 'flex-end' : 'flex-start',
                                                flexDirection: isMe ? 'row' : 'row-reverse',
                                                maxWidth: '85%',
                                            }}
                                        >
                                            <Tooltip title="Reply">
                                                <button
                                                    type="button"
                                                    className="chat-reply-btn"
                                                    onClick={() => handleReplyClick(item)}
                                                >
                                                    ↩
                                                </button>
                                            </Tooltip>

                                            <div
                                                style={{
                                                    backgroundColor: isMe ? '#D3EDF9' : '#ffffff',
                                                    borderRadius: '8px',
                                                    padding: '8px 12px',
                                                    boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                                                    position: 'relative',
                                                    minWidth: '140px'
                                                }}
                                            >
                                                <div style={{ fontSize: '11px', fontWeight: 600, color: isMe ? '#075e54' : '#128c7e', marginBottom: '2px' }}>
                                                    {isMe ? 'You' : item.user_name || item.user_type || 'User'}
                                                </div>

                                                {quoteData && (
                                                    <div 
                                                        style={{
                                                            background: isMe ? '#b2e9e4' : '#f0f2f5',
                                                            borderLeft: '4px solid #128c7e',
                                                            borderRadius: '4px',
                                                            padding: '4px 8px',
                                                            marginBottom: '6px',
                                                            fontSize: '11px',
                                                            color: '#4a4a4a'
                                                        }}
                                                    >
                                                        <span style={{ fontWeight: 600, color: '#128c7e', display: 'block' }}>
                                                            {quoteData.replyUser}
                                                        </span>
                                                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '220px' }}>
                                                            {quoteData.replyText}
                                                        </span>
                                                    </div>
                                                )}

                                                {hasRealAttachment && attachFileInfo && (
                                                    <div className="mb-2">
                                                        {isAttachImage ? (
                                                            <div className="mt-1">
                                                                <Image
                                                                    src={item.attachment!}
                                                                    alt={attachFileInfo.fileName}
                                                                    style={{
                                                                        maxWidth: '100%',
                                                                        maxHeight: '160px',
                                                                        borderRadius: '6px',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div 
                                                                className="p-2 border rounded d-flex align-items-center justify-content-between"
                                                                style={{ background: isMe ? 'rgba(255,255,255,0.7)' : '#f8f9fa' }}
                                                            >
                                                                <div className="d-flex align-items-center me-2 overflow-hidden">
                                                                    <span className="badge bg-secondary me-2 text-uppercase" style={{ fontSize: '10px' }}>
                                                                        {attachFileInfo.ext}
                                                                    </span>
                                                                    <span className="text-truncate" style={{ maxWidth: '140px', fontSize: '12px' }} title={attachFileInfo.fileName}>
                                                                        {attachFileInfo.fileName}
                                                                    </span>
                                                                </div>
                                                                <a 
                                                                    href={item.attachment!} 
                                                                    download={attachFileInfo.fileName}
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-sm btn-outline-primary py-0 px-2"
                                                                    style={{ fontSize: '11px' }}
                                                                >
                                                                    Download
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {(quoteData ? quoteData.actualText : item.message) && (
                                                    <div style={{ fontSize: '13px', color: '#111b21', wordBreak: 'break-word' }}>
                                                        {quoteData ? quoteData.actualText : item.message}
                                                    </div>
                                                )}

                                                <div style={{ textAlign: 'right', fontSize: '10px', color: '#667781', marginTop: '4px' }}>
                                                    <span style={{ marginRight: '6px' }}>
                                                        {dayjs(item.created_on).format('MMM D, YYYY')}
                                                    </span>
                                                    <span>
                                                        {dayjs(item.created_on).format('hh:mm A')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })
                        ) : !getTicketConversationLoading && !saveTicketConversationLoading ? (
                            <div className="text-center text-muted my-auto" style={{ fontSize: '13px' }}>
                                No conversation yet. Start the conversation below.
                            </div>
                        ) : null}
                        <div ref={chatEndRef} />
                    </div>

                    {/* Chat Footer */}
                    <div className="border-top bg-light flex-shrink-0">
                        {replyingTo && (
                            <div 
                                className="px-3 py-2 d-flex align-items-center justify-content-between"
                                style={{
                                    backgroundColor: '#edf5fd',
                                    borderLeft: '4px solid #1890ff',
                                    borderBottom: '1px solid #e8e8e8'
                                }}
                            >
                                <div style={{ fontSize: '12px', overflow: 'hidden' }}>
                                    <span style={{ fontWeight: 600, color: '#1890ff', display: 'block' }}>
                                        Replying to {replyingTo.user_name || replyingTo.user_type || 'User'}
                                    </span>
                                    <span className="text-muted text-truncate d-block" style={{ maxWidth: '300px' }}>
                                        {replyingTo.message.replace(/\[\[REPLY_TO:[^\]]+\]\]\n/g, '')}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleCancelReply}
                                    className="btn btn-sm btn-link text-muted p-0 ms-2"
                                    style={{ textDecoration: 'none', fontSize: '14px', lineHeight: 1 }}
                                    title="Cancel reply"
                                >
                                    ✕
                                </button>
                            </div>
                        )}

                        {chatFile && (
                            <div 
                                className="px-3 py-2 d-flex align-items-center justify-content-between"
                                style={{
                                    backgroundColor: '#fffbe6',
                                    borderLeft: '4px solid #faad14',
                                    borderBottom: '1px solid #ffe58f'
                                }}
                            >
                                <div className="d-flex align-items-center overflow-hidden" style={{ fontSize: '12px' }}>
                                    <span className="me-2 d-inline-flex">
                                        <PaperclipIcon size={15} color="#faad14" />
                                    </span>
                                    <span className="fw-semibold text-truncate" style={{ maxWidth: '240px' }} title={chatFile.name}>
                                        {chatFile.name}
                                    </span>
                                    <span className="text-muted ms-2" style={{ fontSize: '11px' }}>
                                        ({(chatFile.size / 1024).toFixed(1)} KB)
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleRemoveFile}
                                    className="btn btn-sm btn-link text-danger p-0 ms-2"
                                    style={{ textDecoration: 'none', fontSize: '14px', lineHeight: 1 }}
                                    title="Remove attachment"
                                >
                                    ✕
                                </button>
                            </div>
                        )}

                        <div className="p-2 d-flex align-items-center gap-2">
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                style={{ display: 'none' }} 
                                accept=".png,.jpg,.jpeg,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                                onChange={handleFileChange}
                            />
                            
                            <Tooltip title="Attach file">
                                <button
                                    type="button"
                                    className="chat-attach-btn flex-shrink-0"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={saveTicketConversationLoading}
                                >
                                    <PaperclipIcon size={19} color="#595959" />
                                </button>
                            </Tooltip>

                            <Input
                                ref={inputRef}
                                placeholder={
                                    replyingTo 
                                        ? "Type your reply..." 
                                        : chatFile 
                                        ? "Add a caption/message (optional)..." 
                                        : "Type a message..."
                                }
                                value={newMessage}
                                onChange={(e: any) => setNewMessage(e.target.value)}
                                onPressEnter={handleSendMessage}
                                disabled={saveTicketConversationLoading}
                                style={{ borderRadius: '20px' }}
                            />

                            <Button 
                                type="primary" 
                                onClick={handleSendMessage} 
                                loading={saveTicketConversationLoading}
                                disabled={!newMessage.trim() && !chatFile}
                                style={{ borderRadius: '50px', background: '#075e54' }}
                            >
                                Send
                            </Button>
                        </div>
                    </div>
                </div>

                <div 
                    className="detail-tracking-card bg-white p-3 rounded shadow-sm d-flex flex-column visible-scrollbar"
                    style={{ height: '680px', overflowY: 'scroll', paddingRight: '10px' }}
                >
                    <h6 className="panel-title fw-bold mb-3 flex-shrink-0">Ticket tracking</h6>

                    {history.length > 0 ? (
                        <div className="tracking-timeline">
                            {history.map((entry, idx) => {
                                const cfg = getStatusConfig(entry.status);
                                const trackingStyles = getInlineStatusStyle(entry.status);
                                const isLast = idx === history.length - 1;
                                const currentStatus = (entry.status || '').toLowerCase().trim();

                                return (
                                    <div key={`${entry.status}-${entry.changed_on}-${idx}`} className="tracking-item">
                                        <div className="tracking-rail">
                                            <span 
                                                className={`tracking-dot ${cfg.className}`} 
                                                style={{ backgroundColor: trackingStyles.dot }}
                                            />
                                            {!isLast ? <span className="tracking-line" /> : null}
                                        </div>
                                        <div className="tracking-content">
                                            <div className="tracking-top">
                                                <span 
                                                    className={`status-badge ${cfg.className}`}
                                                    style={{
                                                        backgroundColor: trackingStyles.bg,
                                                        color: trackingStyles.text,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        padding: '4px 12px',
                                                        borderRadius: '50px',
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        textTransform: 'capitalize'
                                                    }}
                                                >
                                                    <span 
                                                        className="status-dot" 
                                                        style={{
                                                            backgroundColor: trackingStyles.dot,
                                                            width: '7px',
                                                            height: '7px',
                                                            borderRadius: '50%',
                                                            marginRight: '6px',
                                                            display: 'inline-block'
                                                        }}
                                                    />
                                                    {cfg.label}
                                                </span>
                                                {isLast ? <span className="current-tag ms-1">Current</span> : null}
                                            </div>
                                            <span className="tracking-date">{formatTrackingDate(entry.status, entry.changed_on)}</span>
                                            {entry.note ? <p className="tracking-note">{entry.note}</p> : null}
                                            {entry.changed_by ? (
                                                <span className="tracking-by">
                                                    by {['open', 'closed'].includes(currentStatus)
                                                        ? (record?.requester_name || 'Requester') 
                                                        : 'Support User'}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-muted">No tracking history</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TicketDetails;